import React from "react";
import { Box, type BoxProps, Grid, Icon, Link, Text } from "@chakra-ui/react";
import { FiExternalLink, FiMail } from "react-icons/fi";

export interface DefinitionItem {
  label: string;
  /** null / undefined / "" render as "—". */
  value: React.ReactNode;
  /** Renders the value as a link (mailto: gets a mail icon). */
  href?: string;
}

export interface DefinitionListProps extends BoxProps {
  items: DefinitionItem[];
  /** 1 (default) or 2 columns from md. */
  columns?: 1 | 2;
  /** Uppercase group label above the list, e.g. "YOUR BIDFOOD TEAM". */
  eyebrow?: string;
}

const isEmpty = (v: React.ReactNode) => v === null || v === undefined || (typeof v === "string" && v.trim() === "");

/**
 * Real <dl> of label/value pairs with hairlines — replaces read-only inputs in view modals and the user popover.
 * @example <DefinitionList eyebrow="YOUR BIDFOOD TEAM" columns={2} items={[{ label: "BDE", value: bde }, { label: "BDE Email", value: email, href: `mailto:${email}` }]} />
 */
export const DefinitionList: React.FC<DefinitionListProps> = ({ items, columns = 1, eyebrow, ...rest }) => (
  <Box {...rest}>
    {eyebrow && (
      <Text textStyle="label" color="text.muted" mb={3}>
        {eyebrow}
      </Text>
    )}
    <Grid as="dl" templateColumns={{ base: "1fr", md: `repeat(${columns}, minmax(0, 1fr))` }} columnGap={6} rowGap={0} m={0}>
      {items.map((item, i) => {
        const empty = isEmpty(item.value);
        const isMail = item.href?.startsWith("mailto:");
        return (
          <Box key={`${item.label}-${i}`} py={3} borderBottom="1px solid" borderColor="border.default" minW={0}>
            <Text as="dt" textStyle="label" color="text.muted" mb={1}>
              {item.label}
            </Text>
            <Text as="dd" textStyle="body.sm" fontWeight={600} color={empty ? "text.muted" : "text.primary"} m={0} wordBreak="break-word">
              {empty ? (
                "—"
              ) : item.href ? (
                <Link
                  href={item.href}
                  color="text.link"
                  fontWeight={600}
                  display="inline-flex"
                  alignItems="center"
                  gap={1.5}
                  _hover={{ color: "navy.600", textDecoration: "underline" }}
                  _focusVisible={{ boxShadow: "outline", borderRadius: "sm" }}
                  isExternal={!isMail}
                >
                  <Icon as={isMail ? FiMail : FiExternalLink} boxSize="14px" aria-hidden />
                  {item.value}
                </Link>
              ) : (
                item.value
              )}
            </Text>
          </Box>
        );
      })}
    </Grid>
  </Box>
);
